import {postWithAuth, getWithAuth} from "./HttpMethods";
import {BASE_URL, UPLOAD_URL_V2} from "../../config/config";

const fetchUploadedFiles = async (setUploadedFiles) => {
    try {
        const response = await getWithAuth(UPLOAD_URL_V2);
        const data = await response.json();
        setUploadedFiles(data);
    } catch (err) {
        console.error(err);
    }
};

const fetchUploadedFilesPaginated = async (page = 0, size = 20, tags = null) => {
    try {
        let url, response;

        if (tags && tags.length > 0) {
            // Use POST request with by_tags endpoint when tags are provided
            url = `${UPLOAD_URL_V2}/by_tags?page=${page}&size=${size}`;
            response = await postWithAuth(
                url,
                JSON.stringify(tags),
                {
                    headers: {
                        "Content-Type": "application/json"
                    }
                }
            );
        } else {
            // Use GET request to regular endpoint when no tags
            url = `${UPLOAD_URL_V2}?page=${page}&size=${size}`;
            response = await getWithAuth(url);
        }

        if (!response.ok) {
            throw new Error(`Failed to fetch files: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (err) {
        console.error("Error fetching paginated files:", err);
        throw err;
    }
};

const fetchFileLinkMap = async (fileIds) => {
    try {
        const response = await postWithAuth(
            `${BASE_URL}/links/search/file_link_map`,
            JSON.stringify(fileIds),
            {
                headers: {
                    "Content-Type": "application/json"
                }
            }
        );
        const data = await response.json();
        return data;
    } catch (error) {
        console.error(error);
        throw error;
    }
}

const fetchPDF = async (setFileData, url) => {
    const response = await getWithAuth(url);
    const blob = await response.blob();
    const fileURL = URL.createObjectURL(blob);
    setFileData(fileURL);
};

const fetchThumbnailsByFileIds = async (fileIds = []) => {
    const response = await postWithAuth(
        `${UPLOAD_URL_V2}/thumbnails`,
        JSON.stringify(fileIds),
        {"headers": {"Content-Type": "application/json"}}
    );

    if (!response.ok) throw new Error("Thumbnail fetch failed");

    const contentType = response.headers.get("Content-Type");
    const boundaryMatch = contentType?.match(/boundary=([^;]+)/);
    if (!boundaryMatch) throw new Error("Missing boundary in Content-Type");

    const boundary = boundaryMatch[1];
    const raw = await response.arrayBuffer();
    const decoder = new TextDecoder("utf-8");
    const bytes = new Uint8Array(raw);
    const boundaryBytes = new TextEncoder().encode(`--${boundary}`);

    const parts = [];
    let offset = 0;

    while (offset < bytes.length) {
        const boundaryIndex = findBoundary(bytes, boundaryBytes, offset);
        if (boundaryIndex === -1) break;

        const nextBoundaryIndex = findBoundary(
            bytes,
            boundaryBytes,
            boundaryIndex + boundaryBytes.length
        );

        const isFinal = nextBoundaryIndex === -1;
        const partEnd = isFinal ? bytes.length : nextBoundaryIndex - 2;
        const partBytes = bytes.slice(
            boundaryIndex + boundaryBytes.length + 2,
            partEnd
        );

        // 🩺 Skip empty or non-content parts (like --boundary--)
        const headerPreview = decoder.decode(partBytes.slice(0, 300));
        if (!/Content-ID:|Content-Type:/i.test(headerPreview)) {
            // console.log("⏭ Skipping control or empty part:", headerPreview.trim());
            if (isFinal) break;
            offset = nextBoundaryIndex;
            continue;
        }

        parts.push(partBytes);
        if (isFinal) break;

        offset = nextBoundaryIndex;
    }

    const results = {};

    for (const part of parts) {
        const doubleCRLF = new TextEncoder().encode("\r\n\r\n");
        const headersEnd = findBoundary(part, doubleCRLF, 0);

        if (headersEnd === -1) {
            // console.warn("⚠️ No CRLF boundary found in part — possible malformed part");
            continue;
        }

        const headerBytes = part.slice(0, headersEnd);
        const bodyBytes = part.slice(headersEnd + 4);
        const headersText = decoder.decode(headerBytes);

        // console.log("🔍 Parsed headers:\n" + headersText);

        const contentIdMatch = headersText.match(/Content-ID:\s*([^\r\n]+)/i);
        const contentTypeMatch = headersText.match(/Content-Type:\s*([^\r\n]+)/i);

        if (!contentIdMatch || !contentTypeMatch) {
            // console.warn("❌ Missing expected headers (Content-ID or Content-Type):\n", headersText);
            continue;
        }

        const contentId = contentIdMatch[1].trim();
        const mime = contentTypeMatch[1].trim();

        const blob = new Blob([bodyBytes], {type: mime});
        results[contentId] = URL.createObjectURL(blob);

        // console.log(`✅ Stored thumbnail for contentId: ${contentId} with MIME type: ${mime}`);
    }

    return results;
};


function findBoundary(haystack, needle, start) {
    for (let i = start; i <= haystack.length - needle.length; i++) {
        let match = true;
        for (let j = 0; j < needle.length; j++) {
            if (haystack[i + j] !== needle[j]) {
                match = false;
                break;
            }
        }
        if (match) return i;
    }
    return -1;
}

const fetchPublicVideoLinks = async (page = 0, size = 20) => {
    try {
        const url = `${BASE_URL}/public/files/videos/listed_links?page=${page}&size=${size}`;
        const response = await fetch(url);

        if (!response.ok) {
            throw new Error(`Failed to fetch public video links: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (err) {
        console.error("Error fetching public video links:", err);
        throw err;
    }
};

const fetchThumbnailsByLinkIds = async (linkIds = []) => {
    const response = await fetch(
        `${BASE_URL}/public/files/videos/links/thumbnails`,
        {
            method: 'POST',
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify(linkIds)
        }
    );

    if (!response.ok) throw new Error("Thumbnail fetch failed");

    const contentType = response.headers.get("Content-Type");
    const boundaryMatch = contentType?.match(/boundary=([^;]+)/);
    if (!boundaryMatch) throw new Error("Missing boundary in Content-Type");

    const boundary = boundaryMatch[1];
    const raw = await response.arrayBuffer();
    const decoder = new TextDecoder("utf-8");
    const bytes = new Uint8Array(raw);
    const boundaryBytes = new TextEncoder().encode(`--${boundary}`);

    const parts = [];
    let offset = 0;

    while (offset < bytes.length) {
        const boundaryIndex = findBoundary(bytes, boundaryBytes, offset);
        if (boundaryIndex === -1) break;

        const nextBoundaryIndex = findBoundary(
            bytes,
            boundaryBytes,
            boundaryIndex + boundaryBytes.length
        );

        const isFinal = nextBoundaryIndex === -1;
        const partEnd = isFinal ? bytes.length : nextBoundaryIndex - 2;
        const partBytes = bytes.slice(
            boundaryIndex + boundaryBytes.length + 2,
            partEnd
        );

        const headerPreview = decoder.decode(partBytes.slice(0, 300));
        if (!/Content-ID:|Content-Type:/i.test(headerPreview)) {
            if (isFinal) break;
            offset = nextBoundaryIndex;
            continue;
        }

        parts.push(partBytes);
        if (isFinal) break;

        offset = nextBoundaryIndex;
    }

    const results = {};

    for (const part of parts) {
        const doubleCRLF = new TextEncoder().encode("\r\n\r\n");
        const headersEnd = findBoundary(part, doubleCRLF, 0);

        if (headersEnd === -1) {
            continue;
        }

        const headerBytes = part.slice(0, headersEnd);
        const bodyBytes = part.slice(headersEnd + 4);
        const headersText = decoder.decode(headerBytes);

        const contentIdMatch = headersText.match(/Content-ID:\s*([^\r\n]+)/i);
        const contentTypeMatch = headersText.match(/Content-Type:\s*([^\r\n]+)/i);

        if (!contentIdMatch || !contentTypeMatch) {
            continue;
        }

        const contentId = contentIdMatch[1].trim();
        const mime = contentTypeMatch[1].trim();

        const blob = new Blob([bodyBytes], {type: mime});
        results[contentId] = URL.createObjectURL(blob);
    }

    return results;
};

export {fetchUploadedFiles, fetchUploadedFilesPaginated, fetchPDF, fetchThumbnailsByFileIds, fetchFileLinkMap, fetchPublicVideoLinks, fetchThumbnailsByLinkIds};
